import { ComponentProps, useMemo, useRef, useState } from "react";
import { ActionMeta } from "react-select5";
import { useController, useFormContext } from "react-hook-form";
import { filterOptionDefault, MultiCollapsible } from "@bonliva-crm/ui";
import locale from "@bonliva-crm/locale";
import { useGlobalStore, useUpdateEffect } from "@hooks";
import {
  formatRegionLabel,
  getRegions
} from "@consultants/consultant-profile-new/general-interest/utils";
import { FilterWithFavouriteProps } from "../utils/filters-record-types";
import { ControllableCollapsibleFavFilter } from "./_base/controllable-collapsible-fav-filter";

export type CanWorkInRegionsFilterProps = { label?: string } & FilterWithFavouriteProps;

export const CanWorkInRegionsFilter = (props: CanWorkInRegionsFilterProps) => {
  const store = useGlobalStore();
  const [collapsedGroups, setCollapsedGroups] = useState<string[]>([
    "Sweden",
    "Norway",
    "Bonliva Care"
  ]);

  const options = useMemo<LabelValue[]>(() => {
    return getRegions(store.cache.regionsWithBonlivaCare, [
      "Sweden",
      "Norway",
      "Bonliva Care"
    ]);
  }, [store.cache.regionsWithBonlivaCare]);

  const selectRef = useRef(null);
  const { setValue, resetField } = useFormContext();
  const { field, fieldState } = useController({ name: props.name });
  const [expanded, setExpanded] = useState(fieldState.isDirty);
  const title = props.label ?? locale.CanWorkInRegion;
  useUpdateEffect(() => {
    if (expanded) {
      selectRef.current.focus();
    }
  }, [expanded]);

  const clear = () => {
    selectRef.current?.clearValue();
    resetField(props.name);
  };

  const onChange = (options: LabelValue[], actionMeta: ActionMeta<LabelValue>) => {
    const { action, option } = actionMeta;
    let finalRegions = options;

    if (action === "select-option") {
      const country = option.value.split(":")[0];
      if (option.value === `${country}:*`) {
        finalRegions = options.filter((x) => !x.value.startsWith(`${country}:`));
        finalRegions.push(option);
      }
    }

    setValue(
      props.name,
      finalRegions.map((option) => option.value),
      { shouldDirty: true }
    );
  };

  const onCollapse = (option: LabelValue) => {
    const country = option.value.split(":")[0];
    if (collapsedGroups.some((x) => x === country)) {
      setCollapsedGroups([...collapsedGroups.filter((x) => x !== country)]);
    } else {
      collapsedGroups.push(country);
      setCollapsedGroups([...collapsedGroups]);
    }
  };

  const isOptionSelected = (option: LabelValue) => {
    if (!field?.value) return false;

    const country = option.value.split(":")[0];
    if (field?.value?.some((region) => region === `${country}:*`)) {
      return true;
    }

    return field?.value?.some((region) => region === option.value);
  };

  const isOptionDisabled = (option: LabelValue) => {
    const country = option.value.split(":")[0];
    if (option.value === `${country}:*`) {
      return false;
    }

    return field?.value?.some((region) => region === `${country}:*`);
  };

  const filterOption = (option: LabelValue, inputValue?: string) => {
    const country = option.value.split(":")[0];
    if (option.value === `${country}:*`) {
      return true;
    }
    if (inputValue) {
      return filterOptionDefault(option, inputValue);
    }
    return !collapsedGroups.some((x) => x === country);
  };

  const isCollapsible = (option: LabelValue) => option.value.endsWith(":*");

  const isCollapsed = (option: LabelValue) => {
    const country = option.value.split(":")[0];

    return collapsedGroups.some((x) => x === country);
  };

  const isShowCollapsibleOptionBadge = (option: LabelValue) => {
    const country = option.value.split(":")[0];
    return field?.value?.some((region) => region.includes(country));
  };

  const commonProps: ComponentProps<typeof MultiCollapsible<LabelValue>> = {
    selectRef,
    options,
    name: props.name,
    value:
      field?.value?.map((region) => ({
        label: formatRegionLabel(region),
        value: region
      })) ?? [],
    onChange,
    onCollapse,
    isCollapsible,
    isCollapsed,
    filterOption,
    isIndeterminate: isShowCollapsibleOptionBadge,
    isOptionSelected,
    isOptionDisabled,
    hideSelectedOptions: false,
    controlShouldRenderValue: false,
    placeholder: title,
    isSearchable: true,
    maxMenuHeight: 300,
    isClearable: field?.value !== null,
    showBadge: field?.value !== null,
    onClear: () => setValue(props.name, null, { shouldDirty: true }),
    closeMenuOnSelect: false,
    styleProps: {
      menu: {
        position: "absolute"
      }
    }
  };

  if (props.setFavorite) {
    return (
      <ControllableCollapsibleFavFilter
        title={title}
        isDirty={fieldState.isDirty}
        isClearable={fieldState.isDirty}
        isExpanded={expanded}
        onClear={clear}
        favorite={props.favorite}
        onSetFavorite={props.setFavorite}
        onExpand={() => setExpanded(!expanded)}
      >
        <MultiCollapsible<LabelValue>
          options={options}
          name={props.name}
          value={field?.value?.map((region) => ({
            label: formatRegionLabel(region),
            value: region
          }))}
          onChange={onChange}
          hideSelectedOptions={false}
          isSearchable={true}
          maxMenuHeight={300}
          closeMenuOnSelect={false}
          onCollapse={onCollapse}
          isCollapsible={isCollapsible}
          isCollapsed={isCollapsed}
          filterOption={filterOption}
          isOptionSelected={isOptionSelected}
          isOptionDisabled={isOptionDisabled}
          isIndeterminate={isShowCollapsibleOptionBadge}
          selectRef={selectRef}
          isClearable={false}
          controlShouldRenderValue
          openMenuOnFocus
          menuPortalTarget={document.body}
          showBadge={false}
          placeholder={locale.SelectValue}
          menuPlacement="auto"
          styleProps={{
            menu: { showBorder: true },
            valueContainer: { padding: 0 },
            menuPortal: { zIndex: 9999 }
          }}
        />
      </ControllableCollapsibleFavFilter>
    );
  }

  return (
    <MultiCollapsible<LabelValue>
      {...commonProps}
      styleProps={{
        control: { showBorder: true },
        menu: { showBorder: true }
      }}
    />
  );
};
